import React from 'react';
import CodeIcon from '@material-ui/icons/Code';
import DescriptionIcon from '@material-ui/icons/Description';
import FacebookIcon from '@material-ui/icons/Facebook';
import GitHubIcon from '@material-ui/icons/GitHub';
import LanguageIcon from '@material-ui/icons/Language';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import FranzHeadshot from "../../static/images/franz-noel-tanglao.jpg";
import awsCloudCertificationImage from '../../static/images/aws-certified-cloud-practitioner.png';
import csmImage from '../../static/images/seal-csm.png';
import awsSolutionsArchitectAssociateImage from '../../static/images/aws-certified-solutions-architect-associate.png';
import awsDeveloperAssociateImage from '../../static/images/aws-developer-associate.png';
import compTiaSecurtyPlus from "../../static/images/comptia-security-plus.png";

const profileLinks = [
  {label: "Pang", href: "https://pang-app.com", icon: LanguageIcon},
  {label: "LinkedIn", href: "https://www.linkedin.com/in/franznoeltanglao/", icon: LinkedInIcon},
  {label: "GitHub", href: "https://github.com/franznoel", icon: GitHubIcon},
  {
    label: "Stack Overflow",
    href: "https://stackoverflow.com/users/880135/franz-noel",
    icon: CodeIcon,
  },
  {label: "Medium", href: "https://medium.com/@franznoel", icon: DescriptionIcon},
  {label: "Facebook", href: "https://www.facebook.com/franz.tanglao", icon: FacebookIcon},
];

const principles = [
  {
    title: "I build with empathy",
    text: "I like software that feels considerate. Clean architecture matters, but so does helping a teammate, stakeholder, or customer feel less lost inside a complicated process.",
  },
  {
    title: "I think in systems",
    text: "My work spans frontend, backend, infrastructure, data, and automation. I enjoy seeing how all the moving parts connect and where small improvements can unlock a smoother whole.",
  },
  {
    title: "I explain as I go",
    text: "Documentation, mentoring, and thoughtful discussion are part of the craft for me. I would rather make ideas understandable than hide behind jargon.",
  },
];

const focusAreas = [
  "Full-stack product engineering",
  "Cloud systems and automation",
  "IAM, data, and integration work",
  "Calm technical leadership",
];

const impactHighlights = [
  {
    value: "Full stack",
    label: "React, Node.js, Python, APIs, and the glue between them",
  },
  {
    value: "Cloud-ready",
    label: "AWS, Firebase, Google Cloud, Serverless, and Terraform",
  },
  {
    value: "People-first",
    label: "mentorship, documentation, and clear delivery habits",
  },
];

const experienceHighlights = [
  {
    company: "Deloitte",
    role: "Lead Fullstack Developer",
    period: "Dec 2024 to Jun 2025",
    summary:
      "Led full-stack delivery for nationwide healthcare vaccine administration tools using React, Python, Node.js, AWS API Gateway, Cognito, and Serverless, while coordinating closely with project managers and infrastructure integration work.",
  },
  {
    company: "CBRE",
    role: "Principal Fullstack SaaS Developer, IAM Automation Developer",
    period: "Sep 2023 to Dec 2024",
    summary:
      "Built and migrated identity-focused applications for helpdesk support using React, Angular, Python, Node.js, AWS API Gateway, Cognito, Terraform Enterprise, LDAP, and Azure Entra, with GitHub Actions driving CI/CD and infrastructure deployment.",
  },
  {
    company: "Bank of America",
    role: "Senior Applications Developer",
    period: "Oct 2018 to Jan 2020",
    summary:
      "Created financial applications with OpenFin, React, Redux, Node.js, and Python, while mentoring developers, improving engineering practices, and helping distributed teams deliver complex work with more consistency.",
  },
];

const createdSites = [
  {
    name: "Get Assured Inc.",
    category: "Mortgage services",
    description:
      "A customer-focused mortgage website that makes loan options, resources, and application paths easy to find.",
    url: "https://getassuredinc.pang-app.com/",
  },
  {
    name: "Voices of St. Gen",
    category: "Community stories",
    description:
      "A story-led community website created to give people, experiences, and shared perspectives a place to be heard.",
    url: "https://voicesofstgen.com/",
  },
  {
    name: "Dr. Cris Calayan",
    category: "Professional practice",
    description:
      "A polished professional website designed to present Dr. Cris Calayan's work and help visitors connect with the practice.",
    url: "http://drcriscalayan.com/",
  },
  {
    name: "Franz Tanglao",
    category: "Personal portfolio",
    description:
      "A personal portfolio highlighting software engineering experience, technical perspective, and selected work.",
    url: "https://franz.tanglao.org/",
  },
  {
    name: "Camille Villanueva",
    category: "Fashion & e-commerce",
    description:
      "An online storefront showcasing contemporary Filipino fashion, handwoven pieces, and the designer's collections.",
    url: "https://www.camillevillanueva.com/",
  },
  {
    name: "Dr. Jeannie Garcia",
    category: "Professional practice",
    description:
      "A professional website created to introduce Dr. Jeannie Garcia's practice, services, and approach to prospective patients.",
    url: "https://drjeanniegarcia.com/",
  },
  {
    name: "Knights of Columbus Council 14772",
    category: "Community organization",
    description:
      "The council website I maintain to keep members and the wider community connected with its mission, activities, and updates.",
    url: "https://stgenknights.com/",
  },
];

const skillGroups = [
  {
    title: "Core technologies",
    items: [
      "Python",
      "JavaScript",
      "PHP",
      "Java",
      "React",
      "Node.js",
      "HTML/CSS",
    ],
  },
  {
    title: "Cloud and infrastructure",
    items: [
      "AWS",
      "Google Cloud",
      "Firebase",
      "Linux",
      "Apache",
      "Bash scripting",
    ],
  },
  {
    title: "Data and delivery",
    items: [
      "MySQL",
      "PostgreSQL",
      "MongoDB",
      "BigQuery",
      "Jenkins",
      "Artifactory",
    ],
  },
  {
    title: "Team and product work",
    items: [
      "Scrum",
      "Jira Agile",
      "Technical discovery",
      "Documentation",
      "Mentoring",
    ],
  },
];

const certifications = [
  {
    name: "AWS Cloud Practitioner",
    image: awsCloudCertificationImage,
    link: "https://www.credly.com/badges/edb6f265-38e5-4769-91c9-89633e7db84b/public_url",
  },
  {
    name: "Certified ScrumMaster",
    image: csmImage,
    link: "https://bcert.me/sozgtawvn",
  },
  {
    name: "AWS Solutions Architect Associate",
    image: awsSolutionsArchitectAssociateImage,
    link: "https://www.credly.com/earner/earned/badge/e154c78f-f1b0-42ac-9072-04aa6ef59120",
  },
  {
    name: "AWS Developer Associate",
    image: awsDeveloperAssociateImage,
    link: "https://www.credly.com/earner/earned/badge/e154c78f-f1b0-42ac-9072-04aa6ef59120",
  },
  {
    name: "CompTIA Security+",
    image: compTiaSecurtyPlus,
    link: "https://www.credly.com/badges/372b1299-68f2-42f7-9950-6aef2255bc0c/linked_in_profile",
  },
];

function SocialLinks({className = ""}) {
  const classes = ["link-list", className].filter(Boolean).join(" ");

  return (
    <div className={classes}>
      {profileLinks.map((link) => (
        <a
          key={link.label}
          className="link-pill"
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
        >
          <link.icon className="link-pill__icon" />
          {link.label}
        </a>
      ))}
    </div>
  );
}

export default function Profile(props) {
  return (
    <main className="profile-page">
      <section className="hero">
        <div className="hero__content">
          <p className="section-eyebrow">
            Software engineer, systems thinker, collaborative builder
          </p>
          <h1>{props.title}</h1>
          <p className="hero__lede">
            Principal full-stack engineer building dependable software across
            products, cloud platforms, automation, and identity systems.
          </p>
          <SocialLinks className="link-list--hero" />
          <div className="hero__focus" aria-label="Core focus areas">
            {focusAreas.map((area) => (
              <span key={area}>{area}</span>
            ))}
          </div>
          <div className="hero__actions">
            <a className="button button--primary" href="#connect">
              Reach out
            </a>
            <a className="button button--secondary" href="#experience">
              View experience
            </a>
          </div>
          <div className="hero__stats" aria-label="Profile highlights">
            {impactHighlights.map((item) => (
              <div key={item.value} className="hero-stat">
                <span className="hero-stat__value">{item.value}</span>
                <span className="hero-stat__label">{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <aside className="hero__panel">
          <div className="portrait-card">
            <img
              className="portrait-card__image"
              src={FranzHeadshot}
              alt="Franz Noel Tanglao"
            />
            <div className="portrait-card__body">
              <p className="portrait-card__title">
                Engineering with range, care, and follow-through
              </p>
              <p className="portrait-card__text">
                I help teams turn ambiguous technical problems into systems
                people can trust, maintain, and explain.
              </p>
            </div>
          </div>

          <div className="availability-card">
            <p className="availability-card__eyebrow">Best starting points</p>
            <a href="https://www.linkedin.com/in/franznoeltanglao/" target="_blank" rel="noopener noreferrer">
              LinkedIn for work conversations
            </a>
            <a href="https://github.com/franznoel" target="_blank" rel="noopener noreferrer">
              GitHub for code and experiments
            </a>
          </div>

          <div className="quote-card">
            <p className="quote-card__text">
              “The best engineering work is not just functional. It helps people
              move forward with more confidence.”
            </p>
          </div>
        </aside>
      </section>

      <section className="content-grid">
        <article id="story" className="panel panel--feature">
          <div>
            <p className="section-eyebrow">Who I am</p>
            <h2>A builder with range and a calm way of working</h2>
          </div>
          <div className="feature-copy">
            <p>
              I have spent years working across product needs that demand both
              technical depth and practical judgment: creating applications,
              improving older platforms, troubleshooting infrastructure, and
              helping teams deliver with more clarity.
            </p>
            <p>
              What keeps me engaged is the chance to make complexity more
              manageable, support the people around me, and build systems that
              hold up under real use.
            </p>
          </div>
        </article>

        <article id="approach" className="panel">
          <p className="section-eyebrow">How I work</p>
          <div className="principles">
            {principles.map((principle) => (
              <div key={principle.title} className="principle-card">
                <h3>{principle.title}</h3>
                <p>{principle.text}</p>
              </div>
            ))}
          </div>
        </article>

        <article id="experience" className="panel">
          <p className="section-eyebrow">Selected experience</p>
          <h2>Hands-on leadership across product, platform, and delivery</h2>
          <div className="timeline">
            {experienceHighlights.map((item) => (
              <div key={item.company} className="timeline-item">
                <div className="timeline-item__header">
                  <div>
                    <h3>{item.company}</h3>
                    <p className="timeline-item__role">{item.role}</p>
                  </div>
                  <span className="timeline-item__period">{item.period}</span>
                </div>
                <p>{item.summary}</p>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <p className="section-eyebrow">What I bring</p>
          <h2>Broad capability, curated for clarity</h2>
          <div className="skills-grid">
            {skillGroups.map((group) => (
              <div key={group.title} className="skill-group">
                <h3>{group.title}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </article>

        <article id="portfolio" className="panel panel--portfolio">
          <p className="section-eyebrow">Sites I created &amp; maintain</p>
          <h2>
            Web experiences built for distinct people, organizations, and
            audiences
          </h2>
          <p className="portfolio-intro">
            A selection of websites I have created or maintain across financial
            services, community organizations and storytelling, professional
            services, personal branding, and fashion e-commerce.
          </p>
          <div className="portfolio-grid">
            {createdSites.map((site, index) => (
              <a
                className="portfolio-card"
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                key={site.url}
                aria-label={`Visit ${site.name} (opens in a new tab)`}
              >
                <div className="portfolio-card__topline">
                  <span>{site.category}</span>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                </div>
                <div className="portfolio-card__body">
                  <h3>{site.name}</h3>
                  <p>{site.description}</p>
                </div>
                <div className="portfolio-card__link">
                  <span>{new URL(site.url).hostname.replace("www.", "")}</span>
                  <span className="portfolio-card__arrow" aria-hidden="true">
                    ↗
                  </span>
                </div>
              </a>
            ))}
          </div>
        </article>

        <article id="credentials" className="panel">
          <p className="section-eyebrow">Credentials</p>
          <h2>Education and certifications</h2>
          <div className="credentials-grid">
            <div className="education-card">
              <h3>Education</h3>
              <div className="education-entry">
                <p className="education-entry__title">University of Phoenix</p>
                <p className="education-entry__meta">
                  Master of Information Systems, 2015
                </p>
              </div>
              <div className="education-entry">
                <p className="education-entry__title">
                  University of St. La Salle
                </p>
                <p className="education-entry__meta">
                  Bachelor of Science in Computer Science, 2003
                </p>
              </div>
            </div>

            <div className="certification-grid">
              {certifications.map((item) => (
                <a
                  key={item.name}
                  className="certification-card"
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {item.image ? (
                    <img src={item.image} alt={item.name} />
                  ) : (
                    <div
                      className="certification-card__placeholder"
                      aria-hidden="true"
                    >
                      {item.name}
                    </div>
                  )}
                  <span>{item.name}</span>
                </a>
              ))}
            </div>
          </div>
        </article>

        <article id="connect" className="panel panel--accent">
          <p className="section-eyebrow">Connect</p>
          <h2>Find me where I write, build, and share</h2>
          <p>
            If you want to talk about engineering, collaboration, product ideas,
            or simply how to make a technical problem feel less overwhelming,
            these are the best places to start.
          </p>
          <SocialLinks />
        </article>
      </section>
    </main>
  );
}
